import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { probeDesktop, readDesktopSeenCapabilities, type DesktopStatus } from '@/services/desktopConnectionService';
import {
  desktopNoteUrl,
  desktopSharedNoteUrl,
  hasRecentDesktopSighting,
  isHandoffSuppressed,
  isNoteHanded,
  markNoteHanded,
  readHandoffPreference,
  supportsNoteHandoff,
  supportsSharedNoteHandoff,
  SHARED_NOTE_HANDOFF_CAPABILITY,
  writeHandoffPreference,
} from '@/services/desktopNoteHandoffService';

/**
 * `ask` — the interstitial is up and nothing has fired yet.
 * `opening` — the deep link has been sent; the note keeps loading behind it.
 * `idle` — no handoff UI at all, which is also every failure and opt-out path.
 */
export type DesktopHandoffState = 'idle' | 'ask' | 'opening';

export interface DesktopNoteHandoff {
  state: DesktopHandoffState;
  /** The `notego-desktop://` link, or null when this note cannot be handed off. */
  noteUrl: string | null;
  remember: boolean;
  setRemember: (value: boolean) => void;
  /** Anchor click handler for "Open in desktop app" — deliberately does not preventDefault. */
  confirmOpen: () => void;
  stayInBrowser: () => void;
  /** Closes the snackbar in either state without recording a preference. */
  dismiss: () => void;
}

/** What one page is offering the desktop app: a note by id, or a note by share token. */
interface HandoffTarget {
  /** Key for the once-per-tab-session guard — the noteId or the share token, verbatim. */
  id: string | undefined;
  /** The `notego-desktop://` link, or null when this target cannot be handed off. */
  url: string | null;
  /** Whether a probed desktop can accept `url`. Capability list only, never `version`. */
  supports: (status: DesktopStatus | null) => boolean;
  /**
   * Whether an `always` preference plus a recent loopback sighting may fire
   * `url` without a live probe, given the capabilities that sighting
   * advertised. This is what cold-starts an installed-but-idle app. The `note`
   * link takes any sighting, per the contract. A link gated on a newer
   * capability needs that capability in the sighting: a desktop that would drop
   * the link unrouted would leave the page on "Opening in NoteGo…" with
   * nothing coming.
   */
  remembered: (capabilities: readonly string[]) => boolean;
}

/**
 * Decides whether to offer a note to the NoteGo desktop app, per
 * `coordination/contracts/2026-09-09-desktop-note-handoff.md`.
 *
 * Deliberately lighter than `useDesktopConnection`: a loopback probe and the
 * stored preference, no API round trip and no `POST /users/me/desktop-checks`.
 * The account-wide `desktopVersion` describes some computer at some past time
 * and is never evidence about this one.
 *
 * Only fires for notes shared with you by someone else: a note you created
 * already opens wherever you chose to open it, so prompting its owner is pure
 * friction. The signed-out `/public/:token` page never reaches this at all.
 *
 * The page renders regardless of what this returns — no state here blocks,
 * delays, or replaces it.
 */
function useDesktopHandoff(target: HandoffTarget, creatorUserId: string | undefined): DesktopNoteHandoff {
  const { id, url: noteUrl, supports, remembered } = target;
  const userLoggedIn = useAuthStore((state) => state.userLoggedIn);
  const authLoading = useAuthStore((state) => state.loading);
  const userId = useAuthStore((state) => state.userData?.id);
  const { search } = useLocation();
  const [state, setState] = useState<DesktopHandoffState>('idle');
  const [remember, setRemember] = useState(false);
  const controller = useRef<AbortController | null>(null);

  const suppressed = useMemo(() => isHandoffSuppressed(search), [search]);

  /**
   * Sends the deep link. A custom protocol hands off to the OS instead of
   * navigating, so the page is never unloaded and no history entry is created —
   * `assign` is used precisely because it is not `pushState`/`replaceState`.
   */
  const openNow = useCallback(() => {
    if (!noteUrl || !id) return;
    markNoteHanded(id);
    window.location.assign(noteUrl);
  }, [id, noteUrl]);

  useEffect(() => {
    setState('idle');
    setRemember(false);

    // Auth must have resolved and be signed in. `/shared/:token` is reachable
    // signed-out (it is not behind ProtectedRoutes and its data comes from a
    // public endpoint), so this check is the gate, not the route. The
    // signed-out `/public/:token` page never mounts either hook.
    if (authLoading || !userLoggedIn || !id || !noteUrl || suppressed) return;
    // Only notes someone else shared with you. Ownership is resolved from the
    // note's creator, so both ids must be known first — `undefined` here means
    // "not loaded yet", never "not the owner", and answering early would prompt
    // on your own note during the fetch.
    if (!userId || !creatorUserId || userId === creatorUserId) return;
    // Once per target per tab session — returning to a note via in-app
    // navigation must not produce a second OS dialog.
    if (isNoteHanded(id)) return;

    const preference = readHandoffPreference();
    if (preference === 'never') return;

    // Remembered evidence: an `always` preference plus a loopback sighting
    // inside the window. This is the installed-but-not-running case — firing
    // the link is what cold-starts the app. Only when that sighting could route it.
    if (preference === 'always' && hasRecentDesktopSighting() && remembered(readDesktopSeenCapabilities())) {
      setState('opening');
      openNow();
      return;
    }

    const operation = new AbortController();
    controller.current = operation;

    void probeDesktop(operation.signal).then((status) => {
      if (operation.signal.aborted) return;
      // A missing response is never proof the app is absent, and a desktop that
      // lacks this capability is an older build, not an error. Both mean the
      // live path is unavailable, so the note simply opens in the browser.
      if (!supports(status)) return;
      if (preference === 'always') {
        setState('opening');
        openNow();
        return;
      }
      setState('ask');
    });

    return () => operation.abort();
  }, [authLoading, userLoggedIn, userId, creatorUserId, id, noteUrl, supports, remembered, suppressed, openNow]);

  useEffect(() => () => controller.current?.abort(), []);

  const confirmOpen = useCallback(() => {
    if (!id) return;
    if (remember) writeHandoffPreference('always');
    markNoteHanded(id);
    // No navigation here: a real anchor click carries the protocol URL, so the
    // browser's user gesture reaches the OS handler intact.
    setState('opening');
  }, [id, remember]);

  const stayInBrowser = useCallback(() => {
    if (id) markNoteHanded(id);
    // Only an answer to the offer itself. Once the link has fired this button is
    // just a way out, and must not flip a remembered `always` to `never`.
    if (remember && state === 'ask') writeHandoffPreference('never');
    setState('idle');
  }, [id, remember, state]);

  const dismiss = useCallback(() => {
    if (id) markNoteHanded(id);
    setState('idle');
  }, [id]);

  return { state, noteUrl, remember, setRemember, confirmOpen, stayInBrowser, dismiss };
}

// Module-level so the effect's dependency list stays stable across renders.
const anySighting = () => true;
const sightingSupportsSharedNote = (capabilities: readonly string[]) =>
  capabilities.includes(SHARED_NOTE_HANDOFF_CAPABILITY);

/** `/note/:noteId` — hands the note over by id, for a desktop advertising `note-handoff`. */
export function useDesktopNoteHandoff(
  noteId: string | undefined,
  creatorUserId: string | undefined,
): DesktopNoteHandoff {
  const url = useMemo(() => (noteId ? desktopNoteUrl(noteId) : null), [noteId]);
  return useDesktopHandoff({ id: noteId, url, supports: supportsNoteHandoff, remembered: anySighting }, creatorUserId);
}

/**
 * `/shared/:token` — the page a signed-in viewer lands on from a share link.
 * Hands the note over by token, for a desktop advertising `shared-note-handoff`;
 * the `note` link cannot serve here because the desktop resolves it through the
 * owner/invitee-gated `GET /notes/{id}`. The route itself is unauthenticated, so
 * the caller should also gate the interstitial on a signed-in session.
 */
export function useDesktopSharedNoteHandoff(
  token: string | undefined,
  creatorUserId: string | undefined,
): DesktopNoteHandoff {
  const url = useMemo(() => (token ? desktopSharedNoteUrl(token) : null), [token]);
  // A remembered sighting counts only if that desktop advertised
  // `shared-note-handoff`; an older app would drop a `shared` link unrouted.
  return useDesktopHandoff(
    { id: token, url, supports: supportsSharedNoteHandoff, remembered: sightingSupportsSharedNote },
    creatorUserId,
  );
}
